<app-main-layout>
  <div class="bg-neutral-50 min-h-screen">
  <div class="py-6 px-6 max-w-4xl mx-auto">

    <div class="mb-5 flex items-start justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold text-neutral-800">Site Settings</h1>
        <p class="text-sm text-neutral-500 mt-0.5">Switch delivery options, payment methods and gateway refunds on or off. Changes reach the website and the back office within a minute.</p>
      </div>
      @if (settings?.updatedBy) {
        <div class="text-xs text-neutral-400 text-right shrink-0">
          Last changed by {{ settings?.updatedBy }}<br>{{ settings?.updatedAt | date: 'd MMM y, HH:mm' }}
        </div>
      }
    </div>

    @if (loading) {
      <div class="bg-white rounded-xl border border-neutral-200 shadow-sm p-8 text-center text-neutral-500">Loading site settings...</div>
    } @else if (settings) {

      <!-- Delivery options -->
      <div class="bg-white rounded-xl border border-neutral-200 shadow-sm mb-5 overflow-hidden">
        <div class="px-5 pt-5 pb-3">
          <h2 class="text-base font-bold text-neutral-800">Delivery options</h2>
          <p class="text-xs text-neutral-500 mt-0.5">Choose where each option is offered. Orders that already use an option keep it.</p>
        </div>
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-neutral-50 text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">
              <th class="text-left px-5 py-2.5">Option</th>
              <th class="text-center px-4 py-2.5 w-36">Website checkout</th>
              <th class="text-center px-4 py-2.5 w-36">Back office sales</th>
            </tr>
          </thead>
          <tbody>
            @for (o of deliveryOptions; track o.value) {
              <tr class="border-t border-neutral-100">
                <td class="px-5 py-3">
                  <div class="flex items-center gap-3">
                    <mat-icon class="!text-neutral-400 !text-[20px] !h-5 !w-5">{{ o.icon }}</mat-icon>
                    <div>
                      <div class="font-semibold text-neutral-800">{{ o.label }}</div>
                      <div class="text-xs text-neutral-500">{{ o.hint }}</div>
                    </div>
                  </div>
                </td>
                <td class="text-center px-4 py-3">
                  <input type="checkbox" class="h-5 w-5 accent-amber-500 cursor-pointer" [attr.aria-label]="o.label + ' on the website'"
                    [(ngModel)]="settings.delivery[o.value].website">
                </td>
                <td class="text-center px-4 py-3">
                  <input type="checkbox" class="h-5 w-5 accent-amber-500 cursor-pointer" [attr.aria-label]="o.label + ' in the back office'"
                    [(ngModel)]="settings.delivery[o.value].backOffice">
                </td>
              </tr>
            }
          </tbody>
        </table>
        @if (!channelHasOption('website') || !channelHasOption('backOffice')) {
          <div class="mx-5 mb-4 mt-1 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
            Keep at least one delivery option on for {{ !channelHasOption('website') ? 'the website' : 'the back office' }}, otherwise nobody can place an order there.
          </div>
        }
      </div>

      <!-- Payment methods -->
      @if (settings.payments) {
      <div class="bg-white rounded-xl border border-neutral-200 shadow-sm mb-5 overflow-hidden">
        <div class="px-5 pt-5 pb-3">
          <h2 class="text-base font-bold text-neutral-800">Payment methods</h2>
          <p class="text-xs text-neutral-500 mt-0.5">Choose where each way of paying is offered. "Website" covers checkout and the customer's payment page (payment links). Orders already placed keep their method.</p>
        </div>
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-neutral-50 text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">
              <th class="text-left px-5 py-2.5">Method</th>
              <th class="text-center px-4 py-2.5 w-36">Website</th>
              <th class="text-center px-4 py-2.5 w-36">Back office sales</th>
            </tr>
          </thead>
          <tbody>
            @for (o of paymentOptions; track o.value) {
              <tr class="border-t border-neutral-100">
                <td class="px-5 py-3">
                  <div class="flex items-center gap-3">
                    <mat-icon class="!text-neutral-400 !text-[20px] !h-5 !w-5">{{ o.icon }}</mat-icon>
                    <div>
                      <div class="font-semibold text-neutral-800">{{ o.label }}</div>
                      <div class="text-xs text-neutral-500">{{ o.hint }}</div>
                    </div>
                  </div>
                </td>
                <td class="text-center px-4 py-3">
                  @if (o.websiteToo) {
                    <input type="checkbox" class="h-5 w-5 accent-amber-500 cursor-pointer" [attr.aria-label]="o.label + ' on the website'"
                      [(ngModel)]="settings.payments[o.value].website">
                  } @else {
                    <span class="text-xs text-neutral-400">Back office only</span>
                  }
                </td>
                <td class="text-center px-4 py-3">
                  <input type="checkbox" class="h-5 w-5 accent-amber-500 cursor-pointer" [attr.aria-label]="o.label + ' in the back office'"
                    [(ngModel)]="settings.payments[o.value].backOffice">
                </td>
              </tr>
            }
          </tbody>
        </table>
        @if (!channelHasPayment('website') || !channelHasPayment('backOffice')) {
          <div class="mx-5 mb-4 mt-1 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
            Keep at least one payment method on for {{ !channelHasPayment('website') ? 'the website' : 'the back office' }}, otherwise nobody can pay there.
          </div>
        }
      </div>
      }

      <!-- Refunds -->
      <div class="bg-white rounded-xl border border-neutral-200 shadow-sm p-5 mb-5">
        <h2 class="text-base font-bold text-neutral-800">Refunds through the payment gateway</h2>
        <p class="text-xs text-neutral-500 mt-0.5 mb-3">When off, staff can't send refunds for that payment method from an order's Refunds panel. Refund in the gateway's own portal instead.</p>
        <div class="divide-y divide-neutral-100">
          @for (r of refundOptions; track r.value) {
            <label class="flex items-center justify-between gap-4 py-3 cursor-pointer">
              <div>
                <div class="font-semibold text-neutral-800 text-sm">{{ r.label }}</div>
                <div class="text-xs text-neutral-500">{{ r.hint }}</div>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-xs font-semibold" [ngClass]="settings.refunds[r.value] ? 'text-emerald-600' : 'text-neutral-400'">{{ settings.refunds[r.value] ? 'On' : 'Off' }}</span>
                <input type="checkbox" class="h-5 w-5 accent-amber-500 cursor-pointer" [(ngModel)]="settings.refunds[r.value]">
              </div>
            </label>
          }
        </div>
      </div>

      <div class="flex items-center justify-end gap-3">
        @if (dirty) { <span class="text-xs text-amber-700 mr-auto">You have unsaved changes</span> }
        <button type="button" class="px-4 py-2 rounded-xl border border-neutral-200 bg-white text-sm font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-40"
          [disabled]="!dirty || saving" (click)="reset()">Undo changes</button>
        <button type="button" class="px-5 py-2 rounded-xl bg-gradient-to-b from-amber-400 to-amber-500 text-neutral-900 text-sm font-semibold shadow-sm hover:from-amber-300 hover:to-amber-400 disabled:opacity-40"
          [disabled]="!canSave" (click)="save()">{{ saving ? 'Saving...' : 'Save settings' }}</button>
      </div>

      <!-- PayPal tracking catch-up -->
      <div class="bg-white rounded-xl border border-neutral-200 shadow-sm p-5 mt-8">
        <h2 class="text-base font-bold text-neutral-800">PayPal tracking catch-up</h2>
        <p class="text-xs text-neutral-500 mt-0.5 mb-4">
          For PayPal orders that are already Completed or Delayed Completion. Ones PayPal never got tracking for are sent through the normal flow (Sent now, then In transit and Delivered on the usual timer); ones whose last PayPal update failed are sent again. Orders PayPal already has, and collection orders, are left alone. Check first - nothing is sent until you confirm. Sends go in batches of 20; press Send again for the next batch.
        </p>
        <div class="flex flex-wrap items-end gap-3">
          <label class="text-xs font-semibold text-neutral-500">Paid from
            <input type="date" class="block mt-1 px-3 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-800" [(ngModel)]="resyncFrom" (ngModelChange)="onResyncDates()">
          </label>
          <label class="text-xs font-semibold text-neutral-500">to
            <input type="date" class="block mt-1 px-3 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-800" [(ngModel)]="resyncTo" (ngModelChange)="onResyncDates()">
          </label>
          <button type="button" class="px-4 py-2 rounded-xl border border-neutral-200 bg-white text-sm font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-40"
            [disabled]="resyncBusy || !resyncFrom || !resyncTo" (click)="runResync(true)">{{ resyncBusy && !resync ? 'Checking...' : 'Check orders' }}</button>
          @if (resyncPending > 0) {
          <button type="button" class="px-5 py-2 rounded-xl bg-gradient-to-b from-amber-400 to-amber-500 text-neutral-900 text-sm font-semibold shadow-sm hover:from-amber-300 hover:to-amber-400 disabled:opacity-40"
            [disabled]="resyncBusy" (click)="runResync(false)">{{ resyncBusy ? 'Sending...' : 'Send ' + (resyncPending > resync.batchSize ? 'next ' + resync.batchSize + ' of ' : '') + resyncPending + ' to PayPal' }}</button>
          }
        </div>

        @if (resync) {
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
          <div class="rounded-lg bg-neutral-50 px-3 py-2"><div class="text-lg font-bold">{{ resync.total }}</div><div class="text-[11px] text-neutral-500">Completed / delayed PayPal orders</div></div>
          <div class="rounded-lg bg-emerald-50 px-3 py-2"><div class="text-lg font-bold text-emerald-700">{{ resync.alreadySent }}</div><div class="text-[11px] text-neutral-500">Already at PayPal</div></div>
          <div class="rounded-lg bg-amber-50 px-3 py-2"><div class="text-lg font-bold text-amber-700">{{ resync.dryRun ? resync.toSend + resync.toRetry : resync.sent }}</div><div class="text-[11px] text-neutral-500">{{ resync.dryRun ? 'To send' : 'Sent now' }}</div></div>
          <div class="rounded-lg px-3 py-2" [ngClass]="(resync.dryRun ? resync.noPaymentRef : resync.failed) ? 'bg-red-50' : 'bg-neutral-50'"><div class="text-lg font-bold" [ngClass]="(resync.dryRun ? resync.noPaymentRef : resync.failed) ? 'text-red-700' : ''">{{ resync.dryRun ? resync.noPaymentRef : resync.failed }}</div><div class="text-[11px] text-neutral-500">{{ resync.dryRun ? 'No payment ref' : 'Failed' }}</div></div>
        </div>
        @if (resync.results.length) {
        <div class="mt-3 max-h-80 overflow-y-auto border border-neutral-100 rounded-lg">
          <table class="w-full text-xs">
            <thead class="bg-neutral-50 text-neutral-500 sticky top-0"><tr>
              <th class="text-left px-3 py-2">Order</th><th class="text-left px-3 py-2">Paid</th><th class="text-right px-3 py-2">Amount</th><th class="text-left px-3 py-2">Our tracking</th><th class="text-left px-3 py-2">{{ resync.dryRun ? 'Action' : 'Result' }}</th>
            </tr></thead>
            <tbody class="divide-y divide-neutral-100">
              @for (r of resync.results; track r.orderNo) {
              <tr>
                <td class="px-3 py-1.5 font-semibold">#{{ r.orderNo }}</td>
                <td class="px-3 py-1.5">{{ r.paidOn }}</td>
                <td class="px-3 py-1.5 text-right">£{{ r.amount | number:'1.2-2' }}</td>
                <td class="px-3 py-1.5 text-neutral-500">{{ r.tracking }}</td>
                <td class="px-3 py-1.5" [ngClass]="r.outcome ? (r.outcome === 'sent' ? 'text-emerald-700' : r.outcome === 'next batch' ? 'text-amber-700' : 'text-red-700') : (r.action.startsWith('skip') || r.action === 'already-sent' ? 'text-neutral-400' : 'text-amber-700')">{{ r.outcome || resyncActionLabel[r.action] }}</td>
              </tr>
              }
            </tbody>
          </table>
        </div>
        }
        }
      </div>
    }
  </div>
  </div>
</app-main-layout>
